import { useCallback, useEffect, useState } from 'react'
import { login, loginAuto, verifyOtp } from '../api'
import { loginNotify, notifyLoginOutcome, notifyLoginRequestError } from '../loginNotify'
import type { LoginResponse } from '../types'

export interface UseLoginPageOptions {
  autoLoginUserName?: string | null
  autoLoginPassword?: string | null
}

export function useLoginPage(options: UseLoginPageOptions = {}) {
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<LoginResponse | null>(null)
  const [showOtpUi, setShowOtpUi] = useState(false)
  const [loginAttemptId, setLoginAttemptId] = useState<string | null>(null)

  const applyLoginResult = useCallback((result: LoginResponse) => {
    setResponse(result)
    setShowOtpUi(result.showOtpUi === true)
    setLoginAttemptId(result.loginAttemptId ?? null)

    notifyLoginOutcome(result)
  }, [])

  useEffect(() => {
    if (!options.autoLoginUserName || !options.autoLoginPassword) {
      return
    }

    const controller = new AbortController()
    setLoading(true)

    void loginAuto(
      {
        userName: options.autoLoginUserName,
        password: options.autoLoginPassword,
      },
      controller.signal,
    )
      .then(applyLoginResult)
      .catch(notifyLoginRequestError)
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [options.autoLoginUserName, options.autoLoginPassword, applyLoginResult])

  const submitLogin = useCallback(
    async (userName: string, password: string, turnstileToken: string) => {
      const trimmedUser = userName.trim()
      const trimmedPassword = password.trim()

      if (!trimmedUser) {
        loginNotify.userNameRequired()
        return
      }

      if (!trimmedPassword) {
        loginNotify.passwordRequired()
        return
      }

      if (!turnstileToken.trim()) {
        loginNotify.turnstileRequired()
        return
      }

      setLoading(true)
      try {
        const result = await login({
          userName: trimmedUser,
          password: trimmedPassword,
          turnstileToken,
        })
        applyLoginResult(result)
      } catch (e) {
        notifyLoginRequestError(e)
      } finally {
        setLoading(false)
      }
    },
    [applyLoginResult],
  )

  const submitOtp = useCallback(
    async (otp: string) => {
      if (!loginAttemptId) {
        loginNotify.sessionExpired()
        return
      }

      const trimmedOtp = otp.trim()
      if (!trimmedOtp) {
        loginNotify.otpRequired()
        return
      }

      setLoading(true)
      try {
        const result = await verifyOtp({ loginAttemptId, otp: trimmedOtp })
        applyLoginResult(result)
      } catch (e) {
        notifyLoginRequestError(e)
      } finally {
        setLoading(false)
      }
    },
    [applyLoginResult, loginAttemptId],
  )

  return {
    loading,
    response,
    showOtpUi,
    submitLogin,
    submitOtp,
  }
}
